import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { PageLayout } from '@/components/ui/page-layout';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Compass,
  Brain,
  Battery,
  HelpCircle,
  Sparkles,
  ArrowRight,
  MessageCircle,
  Lightbulb,
  Send,
  Heart,
  Moon,
  Star,
  CheckCircle
} from 'lucide-react';

type Step = 'welcome' | 'feeling' | 'need' | 'preference' | 'result';

interface Question {
  id: string;
  text: string;
  icon: React.ReactNode;
  description: string;
  color: string;
}

const feelings: Question[] = [
  { 
    id: 'lost', 
    text: 'Je suis perdu(e) / flou mental', 
    icon: <HelpCircle className="w-6 h-6" />, 
    description: 'Difficulté à voir clair ou à prendre des décisions',
    color: 'from-blue-500/20 to-blue-600/20 hover:from-blue-500/30 hover:to-blue-600/30 text-blue-700'
  },
  { 
    id: 'tired', 
    text: 'Je suis fatigué(e) / en baisse d\'énergie', 
    icon: <Battery className="w-6 h-6" />, 
    description: 'Sensation de fatigue ou d\'épuisement',
    color: 'from-orange-500/20 to-orange-600/20 hover:from-orange-500/30 hover:to-orange-600/30 text-orange-700'
  },
  { 
    id: 'questioning', 
    text: 'Je me pose des questions existentielles', 
    icon: <Brain className="w-6 h-6" />, 
    description: 'Questionnements profonds sur votre chemin',
    color: 'from-purple-500/20 to-purple-600/20 hover:from-purple-500/30 hover:to-purple-600/30 text-purple-700'
  },
  { 
    id: 'overwhelmed', 
    text: 'J\'ai trop d\'envies, je ne sais pas choisir', 
    icon: <Sparkles className="w-6 h-6" />, 
    description: 'Difficulté à prioriser vos désirs',
    color: 'from-pink-500/20 to-pink-600/20 hover:from-pink-500/30 hover:to-pink-600/30 text-pink-700'
  },
  { 
    id: 'curious', 
    text: 'Je suis curieux(se) de mieux me comprendre', 
    icon: <Lightbulb className="w-6 h-6" />, 
    description: 'Envie d\'explorer votre fonctionnement',
    color: 'from-yellow-500/20 to-yellow-600/20 hover:from-yellow-500/30 hover:to-yellow-600/30 text-yellow-700'
  }
];

const needs: Question[] = [
  { 
    id: 'status', 
    text: 'Faire le point sur mon état actuel', 
    icon: <Battery className="w-6 h-6" />, 
    description: 'Comprendre où vous en êtes maintenant',
    color: 'from-green-500/20 to-green-600/20 hover:from-green-500/30 hover:to-green-600/30 text-green-700'
  },
  { 
    id: 'direction', 
    text: 'Avoir une direction ou des réponses', 
    icon: <Compass className="w-6 h-6" />, 
    description: 'Trouver votre chemin',
    color: 'from-indigo-500/20 to-indigo-600/20 hover:from-indigo-500/30 hover:to-indigo-600/30 text-indigo-700'
  },
  { 
    id: 'understanding', 
    text: 'Comprendre mon fonctionnement profond', 
    icon: <Brain className="w-6 h-6" />, 
    description: 'Explorer votre design énergétique',
    color: 'from-violet-500/20 to-violet-600/20 hover:from-violet-500/30 hover:to-violet-600/30 text-violet-700'
  },
  { 
    id: 'guidance', 
    text: 'Être accompagné(e) avec douceur', 
    icon: <Heart className="w-6 h-6" />, 
    description: 'Recevoir un soutien personnalisé',
    color: 'from-rose-500/20 to-rose-600/20 hover:from-rose-500/30 hover:to-rose-600/30 text-rose-700'
  },
  { 
    id: 'embodiment', 
    text: 'Lâcher le mental et revenir au corps', 
    icon: <Moon className="w-6 h-6" />, 
    description: 'Reconnecter avec votre ressenti',
    color: 'from-teal-500/20 to-teal-600/20 hover:from-teal-500/30 hover:to-teal-600/30 text-teal-700'
  }
];

const preferences: Question[] = [
  { 
    id: 'tool', 
    text: 'Un outil interactif rapide', 
    icon: <Brain className="w-6 h-6" />, 
    description: 'Pour un diagnostic immédiat',
    color: 'from-cyan-500/20 to-cyan-600/20 hover:from-cyan-500/30 hover:to-cyan-600/30 text-cyan-700'
  },
  { 
    id: 'reading', 
    text: 'Une lecture complète et personnalisée', 
    icon: <Lightbulb className="w-6 h-6" />, 
    description: 'Pour une analyse approfondie',
    color: 'from-amber-500/20 to-amber-600/20 hover:from-amber-500/30 hover:to-amber-600/30 text-amber-700'
  },
  { 
    id: 'community', 
    text: 'Échanger avec d\'autres personnes', 
    icon: <MessageCircle className="w-6 h-6" />, 
    description: 'Pour partager vos expériences',
    color: 'from-emerald-500/20 to-emerald-600/20 hover:from-emerald-500/30 hover:to-emerald-600/30 text-emerald-700'
  },
  { 
    id: 'message', 
    text: 'Juste recevoir un message pour aujourd\'hui', 
    icon: <Star className="w-6 h-6" />, 
    description: 'Pour une guidance quotidienne',
    color: 'from-fuchsia-500/20 to-fuchsia-600/20 hover:from-fuchsia-500/30 hover:to-fuchsia-600/30 text-fuchsia-700'
  },
  { 
    id: 'guided', 
    text: 'Je ne sais pas encore, je me laisse guider', 
    icon: <Compass className="w-6 h-6" />, 
    description: 'Pour être accompagné(e) pas à pas',
    color: 'from-violet-500/20 to-violet-600/20 hover:from-violet-500/30 hover:to-violet-600/30 text-violet-700'
  }
];

export default function GuidedJourneyPage() {
  const [currentStep, setCurrentStep] = useState<Step>('welcome');
  const [answers, setAnswers] = useState({
    feeling: '',
    need: '',
    preference: ''
  });
  const [showContactForm, setShowContactForm] = useState(false);
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    message: ''
  });

  const getRecommendation = () => {
    if (answers.feeling === 'tired' || answers.need === 'status') {
      return {
        tool: 'barometre',
        title: 'Le Baromètre Énergétique',
        description: 'Prenez 2 minutes pour évaluer votre état du moment et recevoir une première guidance.',
        link: '/barometre',
        color: 'bg-purple-100 text-purple-600',
        icon: <Battery className="w-8 h-8" />
      };
    } else if (answers.feeling === 'questioning' || answers.need === 'understanding') {
      return {
        tool: 'lecture',
        title: 'Une Lecture de Design',
        description: 'Vous semblez prêt(e) pour une lecture complète de votre Design qui vous donnera une vision claire de votre fonctionnement.',
        link: '/lecture-design',
        color: 'bg-amber-100 text-amber-600',
        icon: <Lightbulb className="w-8 h-8" />
      };
    } else {
      return {
        tool: 'forum',
        title: 'La Communauté HED',
        description: 'Rejoignez notre espace d\'échange bienveillant pour partager et apprendre des expériences des autres.',
        link: '/forum',
        color: 'bg-emerald-100 text-emerald-600',
        icon: <MessageCircle className="w-8 h-8" />
      };
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 'welcome':
        return (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="text-center space-y-6 w-full"
          >
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-purple-500/20 to-pink-500/20 blur-3xl -z-10" />
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4 bg-clip-text text-transparent bg-gradient-to-r from-purple-600 to-pink-600 break-words">
                Bienvenue sur votre chemin
              </h1>
            </div>
            <p className="text-sm sm:text-base md:text-lg text-gray-600 mb-8 max-w-2xl mx-auto leading-relaxed break-words">
              Si vous êtes ici, c'est peut-être parce que vous ressentez un besoin, une fatigue,
              une question, ou simplement une curiosité. Mais vous ne savez pas par où commencer…
              et c'est parfaitement ok.
            </p>
            <p className="text-base sm:text-lg text-gray-600 mb-8 font-medium break-words">
              Le HED n'est pas une course. C'est un chemin intérieur.
            </p>
            <Button 
              onClick={() => setCurrentStep('feeling')}
              className="w-full sm:w-auto bg-[#54075B] hover:bg-[#54075B]/90 px-4 sm:px-6 py-3 sm:py-4 text-sm sm:text-base rounded-full shadow-lg hover:shadow-xl transition-all duration-300"
            >
              Commencer le parcours
              <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 ml-2" />
            </Button>
          </motion.div>
        );

      case 'feeling':
      case 'need':
      case 'preference':
        const questions = currentStep === 'feeling' ? feelings : 
                         currentStep === 'need' ? needs : preferences;
        const title = currentStep === 'feeling' ? 'Comment vous sentez-vous en ce moment ?' :
                     currentStep === 'need' ? 'Qu\'est-ce que vous recherchez en priorité ?' :
                     'Quelle approche vous parle le plus ?';
        
        return (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="space-y-6 w-full max-w-full"
          >
            <h2 className="text-xl sm:text-2xl font-bold text-center mb-8 break-words">{title}</h2>
            <div className="grid gap-4 w-full">
              {questions.map((question, index) => (
                <motion.div
                  key={question.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ scale: 1.02 }}
                  className="relative w-full"
                >
                  <Button
                    variant="outline"
                    className={`w-full p-4 sm:p-6 h-auto flex items-start gap-4 text-left transition-all duration-300 rounded-xl overflow-hidden relative whitespace-normal ${
                      answers[currentStep] === question.id 
                        ? `bg-gradient-to-r ${question.color} border-0 shadow-lg` 
                        : 'hover:bg-gradient-to-r hover:shadow-md hover:scale-[1.02] ' + question.color
                    }`}
                    onClick={() => {
                      setAnswers({ ...answers, [currentStep]: question.id });
                      setTimeout(() => {
                        setCurrentStep(
                          currentStep === 'feeling' ? 'need' :
                          currentStep === 'need' ? 'preference' : 'result'
                        );
                      }, 500);
                    }}
                  >
                    <div className="absolute inset-0 bg-gradient-to-r from-white/40 to-transparent animate-shimmer" />
                    
                    <div className={`shrink-0 p-3 rounded-xl bg-white/80 backdrop-blur-sm shadow-sm ${
                      answers[currentStep] === question.id 
                        ? 'ring-2 ring-white/50'
                        : ''
                    }`}>
                      {question.icon}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-base sm:text-lg break-words">{question.text}</div>
                      <div className="text-sm opacity-90 mt-1 break-words">{question.description}</div>
                    </div>

                    {answers[currentStep] === question.id && (
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        className="absolute top-2 right-2 w-6 h-6 bg-white rounded-full flex items-center justify-center shadow-sm shrink-0"
                      >
                        <CheckCircle className="w-4 h-4 text-green-600" />
                      </motion.div>
                    )}
                  </Button>
                </motion.div>
              ))}
            </div>
          </motion.div>
        );

      case 'result':
        const recommendation = getRecommendation();
        return (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="space-y-8 w-full"
          >
            <div className="text-center">
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.2 }}
                className="inline-block p-3 bg-white rounded-2xl shadow-xl mb-4"
              >
                <Star className="w-10 h-10 text-purple-600" />
              </motion.div>
              <h2 className="text-xl sm:text-2xl font-bold mb-4">Voici notre suggestion pour vous</h2>
              <p className="text-gray-600">
                Basé sur vos réponses, voici le point de départ qui nous semble le plus adapté
                pour vous en ce moment.
              </p>
            </div>

            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.4 }}
            >
              <Card className={`${recommendation.color} border-2 hover:shadow-lg transition-all duration-300`}>
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="shrink-0 p-3 bg-white rounded-xl shadow-sm">
                      {recommendation.icon}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-xl font-bold mb-2 break-words">{recommendation.title}</h3>
                      <p className="text-gray-700 mb-4 break-words">{recommendation.description}</p>
                      <Button 
                        asChild
                        className="bg-[#54075B] hover:bg-[#54075B]/90 shadow-lg hover:shadow-xl transition-all duration-300"
                      >
                        <a href={recommendation.link} className="flex items-center gap-2">
                          Commencer
                          <ArrowRight className="w-4 h-4" />
                        </a>
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.6 }}
              className="text-center"
            >
              <p className="text-gray-600 mb-4">Cette suggestion ne vous parle pas ?</p>
              <Button
                variant="outline"
                onClick={() => setShowContactForm(true)}
                className="hover:bg-purple-50 transition-all duration-300"
              >
                Expliquez-nous votre besoin
                <MessageCircle className="w-4 h-4 ml-2" />
              </Button>
            </motion.div>

            <AnimatePresence>
              {showContactForm && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <Card className="overflow-hidden">
                    <CardContent className="p-6">
                      <h3 className="text-xl font-bold mb-4">Parlez-nous de votre besoin</h3>
                      <form className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Nom
                          </label>
                          <input
                            type="text"
                            value={contactForm.name}
                            onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                            className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Email
                          </label>
                          <input
                            type="email"
                            value={contactForm.email}
                            onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                            className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Votre message
                          </label>
                          <textarea
                            value={contactForm.message}
                            onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                            className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                            rows={4}
                            placeholder="Expliquez-nous ce que vous traversez..."
                          />
                        </div>
                        <Button className="w-full bg-[#54075B] hover:bg-[#54075B]/90 shadow-lg hover:shadow-xl transition-all duration-300">
                          <Send className="w-4 h-4 mr-2" />
                          Envoyer
                        </Button>
                      </form>
                    </CardContent>
                  </Card>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        );
    }
  };

  return (
    <PageLayout>
      <div className="relative overflow-x-hidden w-full">
        <div className="absolute inset-0 z-0">
          <div className="absolute top-0 left-0 w-72 h-72 bg-purple-500/10 rounded-full mix-blend-multiply filter blur-xl animate-blob"></div>
          <div className="absolute top-0 right-0 w-72 h-72 bg-pink-500/10 rounded-full mix-blend-multiply filter blur-xl animate-blob animation-delay-2000"></div>
          <div className="absolute bottom-0 left-0 w-72 h-72 bg-yellow-500/10 rounded-full mix-blend-multiply filter blur-xl animate-blob animation-delay-4000"></div>
        </div>

        <div className="relative z-10">
          <Card className="backdrop-blur-sm bg-white/90 shadow-xl">
            <CardContent className="p-4 sm:p-8">
              {currentStep !== 'welcome' && (
                <div className="flex justify-center gap-4 mb-8">
                  {['feeling', 'need', 'preference', 'result'].map((step, index) => (
                    <motion.div
                      key={step}
                      initial={{ scale: 0.8, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      transition={{ delay: index * 0.1 }}
                      className="flex flex-col items-center gap-2"
                    >
                      <div className={`w-2 sm:w-3 h-2 sm:h-3 rounded-full transition-all ${
                        ['feeling', 'need', 'preference', 'result'].indexOf(currentStep) >= index
                          ? 'bg-purple-600 scale-125'
                          : 'bg-purple-200'
                      }`} />
                      <div className={`h-0.5 w-4 sm:w-8 ${index < 3 ? 'bg-purple-200' : 'hidden'}`} />
                    </motion.div>
                  ))}
                </div>
              )}

              <AnimatePresence mode="wait">
                {renderStep()}
              </AnimatePresence>
            </CardContent>
          </Card>
        </div>
      </div>
      <style jsx>{`
        @keyframes shimmer {
          0% {
            transform: translateX(-100%);
          }
          100% {
            transform: translateX(100%);
          }
        }
        
        .animate-shimmer {
          animation: shimmer 2s infinite;
        }
      `}</style>
    </PageLayout>
  );
}